import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CrosslistSubnav, PageIntro } from "@/components/SellerToolsNav";
import { HIDDEN_SELLER_CHANNELS } from "@/lib/marketplaces";

export default function ImportInventory() {
  const qc = useQueryClient();
  const [importing, setImporting] = useState(false);
  const { data } = useQuery({
    queryKey: ["seller-connections"],
    queryFn: async () => (await api.get("/seller/connections")).data,
  });
  const channels = (data?.channels || []).filter((c) => !c.native && c.connect_via === "unified" && !HIDDEN_SELLER_CHANNELS.has(c.id));

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/seller/crosslist/import.csv", fd);
      toast.success(`Imported ${res.imported} listings as drafts`);
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="seller-import">
      <CrosslistSubnav />
      <PageIntro
        kicker="Crosslist"
        title="Import"
        blurb="CSV with a Title column creates Shoptosa drafts. Include ASIN, UPC/GTIN, or FSN when you will post to Amazon, Shopify, or Flipkart. Connected marketplaces import those IDs through their official API from Connections."
      />
      <div className="bg-aura-surface border border-aura-border rounded-xl p-6 mb-8">
        <div className="font-medium mb-2">CSV</div>
        <p className="text-sm text-aura-muted mb-4">Title, Price, Quantity, Brand, Size, Color, SKU, ASIN, UPC, GTIN, FSN, Images. Amazon/Shopify item_name files also work.</p>
        <label className="inline-flex items-center gap-2 px-4 py-2.5 border border-aura-border rounded-lg cursor-pointer text-sm">
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload CSV
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} />
        </label>
      </div>
      <div className="font-medium mb-3">From a connected marketplace</div>
      <div className="space-y-2">
        {channels.map((c) => (
          <div key={c.id} className="flex items-center justify-between border border-aura-border rounded-lg p-4">
            <div>
              <div className="text-sm">{c.name}</div>
              <div className="text-xs text-aura-muted">{c.connected ? "Connected — Import catalog from Connections" : "Connect first, then import catalog from Connections."}</div>
            </div>
            <Link to={`/seller/connections/${c.id}`} className="text-xs px-3 py-1.5 border border-aura-border rounded-lg">
              {c.connected ? "Import catalog" : "Connect"}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
